import React, { Dispatch, useState } from "react"
import {
  BuilderBlockWithBlockId, DioboxEvent, FixedBlockType, Form,
} from "sharedTypes"
import { Box } from "@chakra-ui/react"
import EditorPanel from "components/Builder/EditorPanel/EditorPanel"
import BuilderLayout from "components/Builder/BuilderLayout"
import { useParams } from "react-router"
import { RichTextFieldType } from "components/ReactHookForm/RichTextField"
import { eventFormUploadsUrl } from "api/forms"
import { DraggableItem } from "sharedTypes/builder"
import { BlockInsertPosition } from "components/Builder/BuildingBlock"
import BuilderContentArea from "components/Builder/BuilderContentArea"
import EditFormModalSettingsPanel from "./EditFormModalSettings/EditFormModalSettingsPanel"
import FormContainer from "../../FormContainer/FormContainer"
import FixedBlockEditorPanel from "../FixedBlockEditorPanel/FixedBlockEditorPanel"

type Props = {
  form: Form
  setForm: Dispatch<React.SetStateAction<Form | undefined>>
  blocks: BuilderBlockWithBlockId[]
  setSelectedBlockId: React.Dispatch<React.SetStateAction<number | undefined>>
  onBlockContentChange: (data: {}, selectedBlockId?: number) => void
  setBlocks: Dispatch<React.SetStateAction<BuilderBlockWithBlockId[]>>
  onDeleteBlock: () => void
  onDuplicateBlock: (blockId: number) => void
  selectedBlockId?: number,
  onAttributeChange: (formData: Form) => void
  event: DioboxEvent
  onDropBlock: (item: DraggableItem, blockId: number, position: BlockInsertPosition) => void
}

const EditFormModalContent = ({
  form,
  setForm,
  blocks,
  onBlockContentChange,
  setSelectedBlockId,
  selectedBlockId,
  setBlocks,
  onDeleteBlock,
  onDuplicateBlock,
  onAttributeChange,
  event,
  onDropBlock,
}: Props) => {
  const selectedBlock = blocks.find(({ blockId }) => blockId === selectedBlockId)
  const [selectedFixedBlockType, setSelectedFixedBlockType] = useState<FixedBlockType>()

  const onSelectFixedBlockType = (blockType: FixedBlockType|undefined) => {
    setSelectedFixedBlockType(blockType)
    if (blockType !== undefined) {
      setSelectedBlockId(undefined)
    }
  }

  const deselectBlocks = () => {
    setSelectedBlockId(undefined)
    setSelectedFixedBlockType(undefined)
  }

  const handleDropIntoDropHereBox = (item: DraggableItem) => {
    if (item.blockTypeToAdd) {
      onDropBlock(item, 0, BlockInsertPosition.Bottom)
    }
  }

  return (
    <BuilderLayout
      sidePanel={(
        <SidePanel
          form={form}
          setForm={setForm}
          selectedBlock={selectedBlock}
          selectedBlockId={selectedBlockId}
          setSelectedBlockId={setSelectedBlockId}
          onBlockContentChange={(data) => onBlockContentChange(data, selectedBlockId)}
          selectedFixedBlokType={selectedFixedBlockType}
          onSelectFixedBlockType={onSelectFixedBlockType}
          onDeleteBlock={onDeleteBlock}
          onAttributeChange={onAttributeChange}
          event={event}
        />
    )}
      handleDropIntoDropHereBox={handleDropIntoDropHereBox}
    >
      <BuilderContentArea onBackdropClick={deselectBlocks}>
        <FormContainer
          form={form}
          blocks={blocks}
          editable
          setSelectedBlockId={setSelectedBlockId}
          selectedBlock={selectedBlock}
          setBlocks={setBlocks}
          onDeleteBlock={onDeleteBlock}
          onSelectFixedBlockType={onSelectFixedBlockType}
          selectedFixedBlockType={selectedFixedBlockType}
          onDuplicateBlock={onDuplicateBlock}
          onDropBlock={onDropBlock}
          portalPrimaryColor={event.portalPrimaryColor}
          mergeTagEventTitle={event.publicEventTitle}
        />
      </BuilderContentArea>
    </BuilderLayout>
  )
}

const SidePanel = ({
  form,
  setForm,
  selectedBlock,
  selectedBlockId,
  setSelectedBlockId,
  onBlockContentChange,
  selectedFixedBlokType,
  onSelectFixedBlockType,
  onDeleteBlock,
  onAttributeChange,
  event,
}) => {
  const { eventId } = useParams<{ eventId: string }>()

  if (selectedBlock) {
    const uploadUrl = eventFormUploadsUrl(eventId, form.id)

    return (
      <EditorPanel
        selectedBlock={selectedBlock.block}
        onChange={onBlockContentChange}
        onCancel={() => setSelectedBlockId(undefined)}
        selectedBlockId={selectedBlockId}
        imageUploadUrl={uploadUrl}
        onDeleteBlock={onDeleteBlock}
        richTextFieldType={RichTextFieldType.FORM_BLOCK_TEXT}
      />
    )
  }

  if (selectedFixedBlokType !== undefined) {
    return (
      <FixedBlockEditorPanel
        form={form}
        setForm={setForm}
        selectedFixedBlockType={selectedFixedBlokType}
        onCancel={() => onSelectFixedBlockType(undefined)}
      />
    )
  }

  return (
    <Box bgColor="gray.100">
      <EditFormModalSettingsPanel
        form={form}
        onAttributeChange={onAttributeChange}
        event={event}
      />
    </Box>
  )
}

export default EditFormModalContent
